/**
 * Better Auth Client -- Expo/React Native
 *
 * Standard module-level singleton following the better-auth/react pattern.
 * Importing from 'better-auth/react' gives us React hooks (useSession, etc.)
 * backed by Nano Stores for automatic reactive session management.
 *
 * The expoClient plugin (native only) handles:
 * - Deep link callback routing (dramadetector://auth/callback/google)
 * - Secure session storage via expo-secure-store
 * - System browser OAuth flow on mobile
 *
 * On web, no plugins are needed — Better Auth uses first-party cookies
 * via the same-origin CF Worker Route.
 *
 * Task: NocoDB #1993 (P0.3c)
 */

import { createAuthClient } from 'better-auth/react';
import { anonymousClient } from 'better-auth/client/plugins';
import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';

// URL patterns for auth endpoints we filter from Sentry. Hoisted to module
// scope so the regex literals are allocated once, not per call (NocoDB #3614).
const SIGN_IN_URL_PATTERN = /\/api\/auth\/sign-in\//;
const SIGN_IN_ANONYMOUS_URL_PATTERN = /\/api\/auth\/sign-in\/anonymous\b/;
const SIGN_UP_URL_PATTERN = /\/api\/auth\/sign-up\//;
const PASSWORD_RESET_URL_PATTERN = /\/api\/auth\/(forget-password|reset-password)/;

/**
 * Predicate for auth endpoint failures that are expected user-input outcomes,
 * not bugs. Used by the BA $fetch onError reporter to filter Sentry noise.
 *
 * - 401 on sign-in: wrong password
 * - 422 on sign-up: validation (duplicate email, weak password)
 * - 404 on forget-password: email not registered
 * - 400 on sign-in/anonymous (DRAMA-WEB-4A): "Anonymous users cannot sign in
 *   again anonymously" — fired when the imperative getSession() race window
 *   in AuthContext returns null but the browser still has a valid anon cookie.
 *   Server correctly rejects the re-anon attempt; AuthContext already handles
 *   the result.error branch by setting status='anonymous'. Capturing this to
 *   Sentry is pure noise (was firing primarily on iOS 13 Mobile Safari, where
 *   the SameSite=None cookie semantics make the race more frequent).
 * - 403 on sign-in/anonymous (DRAMA-WEB-3F): empty-body 403 from the BA
 *   anonymous plugin. Triggered by CSRF/origin/rate-limit gating that BA
 *   applies opaquely — most observed events came from older Mobile Safari
 *   versions with known SameSite=None cookie bugs (rdar://problem/56026923).
 *   The bootstrap effect's catch path already recovers (status='anonymous'),
 *   so capturing the 403 produces no actionable signal — only noise.
 *
 * Real bugs (5xx, network errors, unexpected statuses) still flow to Sentry.
 */
export function isExpectedAuthInputFailure(url: string, status: number | undefined): boolean {
  if (!status) return false;
  if (status === 400 && SIGN_IN_ANONYMOUS_URL_PATTERN.test(url)) return true;
  if (status === 403 && SIGN_IN_ANONYMOUS_URL_PATTERN.test(url)) return true;
  if (status === 401 && SIGN_IN_URL_PATTERN.test(url)) return true;
  if (status === 422 && SIGN_UP_URL_PATTERN.test(url)) return true;
  if (status === 404 && PASSWORD_RESET_URL_PATTERN.test(url)) return true;
  return false;
}

// On web, auth is served on the same domain via CF Worker Route
// (st.dramadetector.com/api/auth/* -> dd-auth worker). Cookies are first-party.
// On native (Expo mobile), call auth.dramadetector.com directly via bearer tokens.
export const BETTER_AUTH_URL = Platform.OS === 'web'
  ? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000')
  : (process.env.EXPO_PUBLIC_BETTER_AUTH_URL ?? 'http://localhost:3000');

// anonymousClient: required so authClient.signIn.anonymous() exists at
// runtime. Registered on both web and native — mirrors the server-side
// `anonymous()` plugin in packages/auth-server/src/auth.ts.
// See EPIC #3840 Phase 2 (#3843).
//
// Typed as `any[]` because the conditional expoClient plugin (native only,
// required dynamically to keep it out of web bundles) prevents TypeScript
// from inferring a single concrete plugins-tuple type at this file level.
// Callers that use plugin-contributed methods annotate `@ts-expect-error`
// at the call site (see apps/web-landing/contexts/AuthContext.tsx usage of
// `authClient.signIn.anonymous()`).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const plugins: any[] = [anonymousClient()];
if (Platform.OS !== 'web') {
  // expoClient plugin: native only. On web, session persistence uses cookies
  // (set by the same-origin Worker Route), not SecureStore/localStorage.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { expoClient } = require('@better-auth/expo/client');
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const SecureStore = require('expo-secure-store');
  plugins.push(
    expoClient({
      scheme: 'dramadetector',
      storage: SecureStore,
      storagePrefix: 'dd-auth',
    }),
  );
}

export const authClient = createAuthClient({
  baseURL: BETTER_AUTH_URL,
  fetchOptions: {
    credentials: 'include',
    onError(context) {
      // @ts-expect-error BA onError context shape varies across versions
      const url: string = context.url ?? context.response?.url ?? 'unknown';
      const status: number | undefined = context.response?.status;
      const msg = `[BA $fetch error] ${url} ${status ?? ''} ${context.error?.message ?? ''}`;
      console.error(msg);

      // Drop expected user-input failures from Sentry. These are validation
      // outcomes (wrong password, duplicate email), not bugs. Tracks DRAMA-WEB-3D.
      // Keep them in console for local debugging; they remain visible as
      // PostHog events via the auth analytics pipeline if needed.
      if (isExpectedAuthInputFailure(url, status)) return;

      if (!__DEV__) {
        try { Sentry.captureMessage(msg, 'warning'); } catch { /* Sentry may not be initialized */ }
      }
    },
  },
  plugins,
});

// Named exports for direct use — standard better-auth pattern
export const { signIn, signUp, signOut, useSession, getSession } = authClient;
